import React from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, useIntl} from 'react-intl'
import {useShopperCustomersMutation, useCustomerId} from '@salesforce/commerce-sdk-react'
import {
    Box,
    Heading,
    Text,
    Button,
    Drawer,
    DrawerBody,
    DrawerOverlay,
    DrawerContent,
    DrawerCloseButton,
    VStack,
    Alert,
    AlertIcon
} from '@salesforce/retail-react-app/app/components/shared/ui'
import Link from '@salesforce/retail-react-app/app/components/link'
import Field from '../../components/field'
import {SMSOptInIcon, CheckCircleIcon} from '../../components/icons'
import useCommunicationsFields from '../../components/forms/useCommunicationsFields'
import {useForm} from 'react-hook-form'

import {sendViewErrorEvent} from '../../utils/gtm-utils'

const SMSOptInModal = ({isOpen, onClose, prefix = ''}) => {
    const {
        mutate: updateAttentiveSMS,
        isLoading,
        isSuccess
    } = useShopperCustomersMutation('updateCustomer')
    const customerId = useCustomerId()
    const {formatMessage} = useIntl()

    const form = useForm({
        defaultValues: {
            [`${prefix}smsPhoneNumber`]: ''
        }
    })

    const fields = useCommunicationsFields({form, prefix})

    const onSubmit = ({smsPhoneNumber}) => {
        updateAttentiveSMS(
            {
                parameters: {
                    customerId
                },
                body: {
                    c_sms_garage_optedIn: true,
                    c_smsOptIn: {optedIn: true, brand: 'garage', phone: smsPhoneNumber}
                }
            },
            {
                onError() {
                    // GTM Modification
                    sendViewErrorEvent('Failed to subscribe to SMS notifications. Please try again.')
                    form.setError('global', {
                        type: 'manual',
                        message: formatMessage({
                            id: 'communication_preferences.sms.error_message',
                            defaultMessage:
                                'Failed to subscribe to SMS notifications. Please try again.'
                        })
                    })
                },
                onSuccess() {
                    form.reset()
                }
            }
        )
    }

    return (
        <Drawer onClose={onClose} isOpen={isOpen} size="md">
            <DrawerOverlay />
            <DrawerContent>
                <DrawerCloseButton />
                <DrawerBody alignContent="start" p={{base: '16px', lg: '32px'}}>
                    {isSuccess ? (
                        <Box mt="80px">
                            <CheckCircleIcon width="38px" height="38px" />
                            <Text
                                fontSize={{base: '20px'}}
                                fontWeight={{base: 700}}
                                lineHeight={{base: '20px'}}
                                textTransform={{base: 'uppercase'}}
                                mt={{base: '24px'}}
                            >
                                <FormattedMessage
                                    defaultMessage="You’ve Signed Up for SMS!"
                                    id="communication_preferences.sms.optin_success_title"
                                />
                            </Text>

                            <Text
                                fontSize={{base: '14px'}}
                                lineHeight={{base: '18px'}}
                                mt={{base: 2}}
                            >
                                <FormattedMessage
                                    defaultMessage={`Thanks! You're almost subscribed to our SMS - confirm your subscription by replying "Y" and your points will be on the way!`}
                                    id="communication_preferences.sms.optin_success_subtitle"
                                />
                            </Text>
                        </Box>
                    ) : (
                        <Box mt={{base: '35px', lg: '64px'}}>
                            <form id="sms-subscription-form" onSubmit={form.handleSubmit(onSubmit)}>
                                <VStack spacing={6}>
                                    <Box textAlign="center">
                                        {form.formState.errors?.global && (
                                            <Alert status="error" maxW={'max-content'} mb={8}>
                                                <AlertIcon color="red.500" boxSize={4} />
                                                <Text fontSize="sm" ml={3}>
                                                    {form.formState.errors.global.message}
                                                </Text>
                                            </Alert>
                                        )}
                                        <SMSOptInIcon w="48px" h="48px" />
                                        <Heading
                                            as="h3"
                                            mt={6}
                                            fontSize={{base: '20px', lg: '32px'}}
                                            textTransform="uppercase"
                                        >
                                            <FormattedMessage
                                                id="communication_preferences.sms.modal_heading"
                                                defaultMessage="Find Out First!"
                                            />
                                        </Heading>
                                    </Box>
                                    <Box>
                                        <Text
                                            fontWeight="bold"
                                            fontSize={{base: 'sm', lg: 'md'}}
                                            textTransform="uppercase"
                                        >
                                            <FormattedMessage
                                                id="communication_preferences.sms.bonus_points"
                                                defaultMessage="Earn 10 bonus points when you subscribe to Garage text alerts"
                                            />
                                        </Text>
                                    </Box>
                                    <Box>
                                        <Text fontSize={{base: 'sm', lg: 'md'}}>
                                            <FormattedMessage
                                                id="communication_preferences.sms.points_disclaimer"
                                                defaultMessage='Points will be granted once you confirm your subscription by replying "Y" to the SMS. Please allow 48 hours for the points to be added to your account. Not applicable to customers who have enabled notifications before joining the Garage Collectif.'
                                            />
                                        </Text>
                                    </Box>
                                    <Box w="full">
                                        <Field {...fields.smsPhoneNumber} />
                                    </Box>
                                    <Box w="full">
                                        <Button
                                            variant="solid"
                                            fontSize="md"
                                            type="submit"
                                            w="full"
                                            isLoading={isLoading}
                                            onClick={() => {
                                                form.clearErrors('global')
                                            }}
                                        >
                                            <FormattedMessage
                                                id="communication_preferences.sms.subscribe"
                                                defaultMessage="Subscribe"
                                            />
                                        </Button>
                                    </Box>
                                    <Box>
                                        <Text fontSize="xs" color="gray.700">
                                            <FormattedMessage
                                                id="communication_preferences.sms.legal_disclaimer"
                                                defaultMessage="By submitting this form, you agree to receive marketing text messages (e.g. cart reminders) from Garage at the cell number used when signing up. Reply HELP for help. You can withdraw your consent at any time by texting STOP. Message frequency varies. Standard message & data rates may apply. {termsLink}. Contact Garage by mail at 5592 Ferrier, Mount-Royal, Quebec, H4P 1M2, Canada or via email at {emailLink}"
                                                values={{
                                                    termsLink: (
                                                        <Link href="/terms-privacy">
                                                            <FormattedMessage
                                                                id="communication_preferences.sms.terms_link_text"
                                                                defaultMessage="View Terms & Privacy"
                                                            />
                                                        </Link>
                                                    ),
                                                    emailLink: (
                                                        <Link href="mailto:customerservice@garageclothing.com">
                                                            <FormattedMessage
                                                                id="communication_preferences.sms.email_link_text"
                                                                defaultMessage="customerservice@garageclothing.com"
                                                            />
                                                        </Link>
                                                    )
                                                }}
                                            />
                                        </Text>
                                    </Box>
                                </VStack>
                            </form>

                        </Box>
                    )}
                </DrawerBody>
            </DrawerContent>
        </Drawer>
    )
}

SMSOptInModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    prefix: PropTypes.string
}

export default SMSOptInModal
